// Portal users — the people who can sign in to the portal: an operator, or
// one of its drivers. Staff hand access out from the Operators screen; this
// service is the only place that talks to the endpoint.
//
// There used to be a third kind, 'dispatcher', for Pickup Hungary's own
// office staff, with its own listing and its own panel. Both are gone:
// dispatchers now open the board by signing in with the dashboard account
// they already have (Pages/Portal/StaffLogin), so no dispatcher login is
// minted anywhere. Rows granted before that still exist until they are
// revoked, which is why 'dispatcher' survives as a LABEL below and as a
// holder in Utils/portalLogins — but never as something to create.
//
// API contract (ph-core Schemas/Zod/PortalUsers.schema.ts):
//   GET    /operators/portal-users?operatorId=…     → PortalUserOutput[]
//   POST   /operators/portal-users
//          { action: 'grant', kind, operator_id?, driver_id?, email, replace_existing? }
//        | { action: 'regenerate', cognito_sub }     → { action, portal_user, enrolment }
//          A grant for an address a DIFFERENT login holds is refused 400
//          { message, code: 'PORTAL_EMAIL_HELD', details: { email, replaceable,
//          holder: { kind, operator_id?, operator_name?, driver_id?, active } } }
//          — see Utils/portalLogins.
//   DELETE /operators/portal-users?cognito_sub=…  → revoke
// Every JSON body arrives enveloped as { success, data }.
//
// The POST response carries the enrolment token EXACTLY ONCE. It is handed
// straight to the hand-over view and dies with the modal: never persist it,
// never put it in a log line, never send it anywhere else.

import { get, post, del } from './API';
import { PORTAL_USER_KINDS } from '../config/config';

export { PORTAL_USER_KINDS };

// The portal the enrolment link points at. The driver-facing portal currently
// ships inside this app under /portal, so the link is same-origin by default.
// Kept as ONE constant: when the portal moves to its own host, set
// REACT_APP_PORTAL_URL (the app's usual config convention) and nothing else
// changes.
const PORTAL_BASE_URL = (
  process.env.REACT_APP_PORTAL_URL || `${window.location.origin}/portal`
).replace(/\/$/, '');

// The single-use link a driver scans; the portal consumes it at /e/<token>.
export const enrolmentUrl = (token) => `${PORTAL_BASE_URL}/e/${token}`;

export const KIND_LABELS = {
  [PORTAL_USER_KINDS.OPERATOR]: 'Operator',
  [PORTAL_USER_KINDS.DRIVER]: 'Driver',
  // Legacy, and kept for exactly that: nothing grants one any more, but the
  // rows from before are still readable and must not render as a blank cell.
  [PORTAL_USER_KINDS.DISPATCHER]: 'Dispatcher',
};

// One PortalUserOutput row → the modal's shape. A revoked login keeps its row
// (`active: 0`), so staff can see that access was taken away.
const toUser = (row) => ({
  cognitoSub: row.cognito_sub,
  kind: row.kind,
  driverId: row.driver_id,
  name: row.driver_name || '',
  email: row.email,
  active: row.active === 1,
  createdBy: row.created_by || '',
  createdAt: row.createdAt || null,
  // An outstanding token means the link was handed over but never scanned.
  enrolmentPending: row.enrolment_pending,
});

// A grant/regenerate response → the hand-over's one-shot token plus the row.
const toGrant = ({ portal_user: portalUser, enrolment }) => ({
  token: enrolment.enrolment_token,
  expiresAt: enrolment.expires_at,
  cognitoSub: enrolment.cognito_sub,
  user: toUser(portalUser),
});

export default {
  // Every portal user of one operator, active and revoked alike.
  list: async (operatorId) => {
    const response = await get('/operators/portal-users', { operatorId });
    return response.data.data.map(toUser);
  },

  // Create a login and mint its first enrolment token, for an OPERATOR or
  // one of its DRIVERS. `operator_id` travels for both, `driver_id` only for
  // a driver. There is no third kind any more: Pickup Hungary's own office
  // staff reach the dispatch board by signing in with their dashboard
  // account, so no dispatcher login is minted here or anywhere.
  //
  // `replaceExisting` is the admin's answer to a PORTAL_EMAIL_HELD refusal:
  // take an address a REVOKED login of somebody else's still holds. It is
  // sent ONLY once they have confirmed it — an ordinary grant carries no
  // such field, so a retry or a double-click can never destroy a login.
  grant: async ({
    operatorId, kind, driverId, email, replaceExisting = false,
  }) => {
    const body = {
      action: 'grant', kind, email, operator_id: operatorId,
    };
    if (kind === PORTAL_USER_KINDS.DRIVER) body.driver_id = String(driverId);
    if (replaceExisting) body.replace_existing = true;
    const response = await post('/operators/portal-users', body);
    return toGrant(response.data.data);
  },

  // Mint a fresh token for someone who already has a login (lost phone, link
  // expired). Invalidates whatever token was outstanding.
  regenerate: async (cognitoSub) => {
    const response = await post('/operators/portal-users', {
      action: 'regenerate', cognito_sub: cognitoSub,
    });
    return toGrant(response.data.data);
  },

  // Disable the login. The signed-in phone loses the portal immediately.
  revoke: async (cognitoSub) => del('/operators/portal-users', { cognito_sub: cognitoSub }),
};
